import React, { ReactNode, useCallback, useEffect, useState, useMemo } from 'react';
import { TreeViewDataItem, TreeView, DrawerPanelContent, DrawerPanelBody } from '@patternfly/react-core';
import { ExclamationCircleIcon, ExclamationTriangleIcon, LockIcon } from '@patternfly/react-icons'
import { useDispatch, useSelector } from 'react-redux';

import './customization-page.css';
import { useTranslation } from 'react-i18next';

import i18next from 'i18next';
import { userSelectors } from '../../store/user/userSlice';
import { CustomizationObjectType, CustomizationTreeViewDataItem, ModelType, TAG_LATEST, ValidationStatus } from '../../store/global-types/customization-types';
import { convertTreeViewDataIntoItemHierarchy, showCustomizationTreeViewDataItem } from '../../store/global-types/customization-helpers';
import { StoreState } from '../../store/store';
import { uniq } from 'lodash-es';
import { modelCustomizationPageFocusCleared } from '../../store/appStatus/appStatusSlice';
import { selectContourTreeViewItems } from '../../store/contouring/contouringSelectors';
import { selectDoseTreeViewItems } from '../../store/dose/doseSelectors';
import { selectImageTreeViewItems } from '../../store/image/imageSelectors';
import CustomizationFilters from './CustomizationFilters';
import { selectAdaptTreeViewItems } from '../../store/adapt/adaptSelectors';
import { getBodyPartIcon } from '../../components/body-part-icon';
import { BODY_PART_DISPLAY_ORDER } from '../../store/global-types/customization-helpers';
import ModelTag, { ModelTagStyle } from '../../components/model-tag';
import CustomizationSearchBar from './CustomizationSearchBar';
import { filterCustomizationTreeByFreeText } from '../../store/global-types/customizationSelectors';
import { getFreeTextFilterAction, getVisibilityStates, getActiveFreeTextFilter } from '../../store/global-types/filter-helpers';

interface CustomizationSideBarProps {
    currentTreeViewFocus: string,
    setCurrentTreeViewFocus: (value: string) => void,
    modelType: ModelType.Contouring | ModelType.Dose | ModelType.Adapt | ModelType.Image,
}

const getTreeViewItemName = (item: CustomizationTreeViewDataItem,
    noLicenseWarning: string,
    tags: string[] | undefined,
    nameOverride: string | undefined,
    isLatest: boolean,
    version: string,
    includeNonLicensedModelsState: boolean,
    includeDeprecatedModelsState: boolean,
    includeUserHiddenModelsState: boolean): ReactNode => {

    // use override if given
    const itemLabel = nameOverride !== undefined ? nameOverride : item.label;

    // show an asterisk if the item has been modified
    const label = `${itemLabel}${item.isModified ? ' *' : ''}`;

    // show item label with the version number if it has one, or if it's the latest version
    // but only for models, not for customizations or output files
    const labelWithVersion =
        (item.type === CustomizationObjectType.Model && (version || isLatest)) ?
            (<span className="label-tag"><span>{label}</span> <ModelTag allowInteraction={true} style={ModelTagStyle.Highlighted} allTags={[version]}>{isLatest ? TAG_LATEST : version}</ModelTag></span>)
            : label;

    // mark the item as unavailable in UI as necessary
    const labelNode = item.isAvailable ? labelWithVersion : (<span className="tree-view-no-license" title={noLicenseWarning}><span className="label">{labelWithVersion}</span><LockIcon /></span>);

    if (item.validationStatus === ValidationStatus.Valid) {
        return labelNode;
    } else {
        // we need to deduce whether this item would've been hidden by default if it was valid
        const isHiddenByDefault = (item.type === CustomizationObjectType.CustomizationBase || item.type === CustomizationObjectType.CustomizationOutput)
            && showCustomizationTreeViewDataItem(item,
                includeNonLicensedModelsState,
                includeDeprecatedModelsState,
                includeUserHiddenModelsState,
                true) === false;

        const title = isHiddenByDefault ? i18next.t('translation:customizationPage.validationError.hiddenByDefault') : undefined;

        const isWarning = item.validationStatus === ValidationStatus.Warning;
        const className = isWarning ? 'tree-view-validation-warning' : 'tree-view-validation-error';
        const icon = isWarning ? <ExclamationTriangleIcon /> : <ExclamationCircleIcon />;

        return (<span className={className}>
            {icon}
            <span className={`label${isHiddenByDefault ? ' is-hidden-by-default' : ''}`} title={title}>{labelNode}</span>
        </span>)
    }
}

/** Recursively find treeview items under and including target. */
const getActiveTreeItemsUnderTarget = (item: TreeViewDataItem, target: TreeViewDataItem, branch: TreeViewDataItem[], isTargetBranch: boolean = false) => {
    const isInTargetBranch = isTargetBranch || item.id === target.id;

    if (isInTargetBranch) {
        branch.push(item);
    }

    if (item.children) {
        item.children.forEach(c => {
            getActiveTreeItemsUnderTarget(c, target, branch, isInTargetBranch);
        });
    }
}

/** Recursively find and collect tree view items from a specific branch up to a set target item. */
const getActiveTreeViewItemBranch = (item: TreeViewDataItem, target: TreeViewDataItem, branch: TreeViewDataItem[]): boolean => {
    if (item.children) {
        let childContained = false;
        item.children.forEach(c => {
            const wasChildContained = getActiveTreeViewItemBranch(c, target, branch);
            childContained = childContained || wasChildContained;
        });

        if (childContained) {
            // collect the branch that we're going down if this one has the child object that's our target
            branch.push(item);
        }
    }

    if (item.id === target.id) {
        branch.push(item);
        return true;
    }

    return false;
}

/** Find a single item from tree view hierarchy. */
const findItemFromHierarchy = (hierarchy: TreeViewDataItem[], id: string, mustBeLeafNode: boolean = false): TreeViewDataItem | undefined => {
    const nextItems = [...hierarchy];
    while (nextItems.length > 0) {
        const next = nextItems.pop();
        if (next) {
            if (next.id && next.id === id && (!mustBeLeafNode || !next.children)) {
                return next;
            }
            if (next.children) { next.children.forEach(c => nextItems.push(c)); }
        }
    }

    return undefined;
}

const CustomizationSideBar = (props: CustomizationSideBarProps) => {

    const { currentTreeViewFocus, setCurrentTreeViewFocus, modelType } = props;

    const { t } = useTranslation();
    const dispatch = useDispatch();


    const noLicenseWarning = t('customizationPage.noLicense.tooltip');

    const [activeTreeViewItems, setActiveTreeViewItems] = useState<TreeViewDataItem[]>([]);
    const [treeViewData, setTreeViewData] = useState<TreeViewDataItem[]>([]);
    const modelCustomizationPageFocusId = useSelector((state: StoreState) => state.appStatus.modelCustomizationPageFocusId);

    // useSelector hooks must be always run in the same order so we have to move them
    // outside of the switch statement below
    const contourCustomizationHierarchy = useSelector(selectContourTreeViewItems);
    const doseCustomizationHierarchy = useSelector(selectDoseTreeViewItems);
    const imageCustomizationHierarchy = useSelector(selectImageTreeViewItems);
    const adaptCustomizationHierarchy = useSelector(selectAdaptTreeViewItems);

    let customizationHierarchy: CustomizationTreeViewDataItem[];
    switch (modelType) {
        case ModelType.Contouring:
            customizationHierarchy = contourCustomizationHierarchy;
            break;
        case ModelType.Dose:
            customizationHierarchy = doseCustomizationHierarchy;
            break;
        case ModelType.Image:
            customizationHierarchy = imageCustomizationHierarchy;
            break;
        case ModelType.Adapt:
            customizationHierarchy = adaptCustomizationHierarchy;
            break;
        default:
            throw new Error('Unsupported model type');
    }

    // Get model visibility state from appropriate slice based on model type
    const { areDeprecatedModelsShown, areNonLicensedModelsShown, areUserHiddenModelsShown } = useSelector(
        (state: StoreState) => getVisibilityStates(state, modelType)
    );

    const includeDeprecatedModelsState = areDeprecatedModelsShown;
    const includeNonLicensedModelsState = areNonLicensedModelsShown;
    const includeUserHiddenModelsState = areUserHiddenModelsShown;

    const handleTreeViewDataUpdate = useCallback((data: TreeViewDataItem[]) => {

        // update tree view data with branches and leaves that should start expanded
        const nextItems = [...data];
        while (nextItems.length > 0) {
            const next = nextItems.pop();
            if (next) {
                if (next.children) { next.children.forEach(c => nextItems.push(c)); }
                if (activeTreeViewItems.find(i => i.id && next.id && i.id === next.id)) {
                    next.defaultExpanded = true;
                }
            }
        }

        setTreeViewData(data);
    }, [activeTreeViewItems]);

    const handleTreeViewItemSelect = useCallback((item: TreeViewDataItem) => {

        // TODO: some of this code updates the 'defaultExpanded' prop directly into objects already in state
        // which is not optimal behaviour.

        // are we selecting or unselecting an item from the current active selection?
        const isSelecting = activeTreeViewItems.find(i => i.id === item.id) === undefined;

        // is this a leaf item? we have some special rules for those.
        const isLeafItem = item.children === undefined;

        // don't allow de-selecting leaf items
        if (!isSelecting && isLeafItem) {
            return;
        }

        // prepare existing tree view selections
        const branchItems: TreeViewDataItem[] = [];
        activeTreeViewItems.forEach(item => {
            if (item.children === undefined) {
                // immediately unselect any leaf items -- this fixes some visual glitches
                item.defaultExpanded = false;
            } else {
                // include non-leaf items in the next active selection
                branchItems.push(item);
            }
        });

        if (!isSelecting) {
            const activeTreeViewItemBranch: TreeViewDataItem[] = [];
            treeViewData.forEach(t => getActiveTreeItemsUnderTarget(t, item, activeTreeViewItemBranch));
            activeTreeViewItemBranch.forEach(i => i.defaultExpanded = false);

            setActiveTreeViewItems(branchItems.filter(i => !activeTreeViewItemBranch.find(branchItem => branchItem.id === i.id)));
        } else {
            const activeTreeViewItemBranch: TreeViewDataItem[] = [];
            treeViewData.forEach(t => getActiveTreeViewItemBranch(t, item, activeTreeViewItemBranch));
            activeTreeViewItemBranch.forEach(i => i.defaultExpanded = true);

            setActiveTreeViewItems(uniq(branchItems.concat(activeTreeViewItemBranch)));
        }

        // set latest focus
        setCurrentTreeViewFocus(isLeafItem && item.id ? item.id : '');

    }, [activeTreeViewItems, treeViewData, setCurrentTreeViewFocus]);

    const handleSelect = useCallback((_: unknown, item: TreeViewDataItem) => {
        if (item.id === undefined) { throw new Error('TreeView item has no ID'); }
        handleTreeViewItemSelect(item);
    }, [handleTreeViewItemSelect]);

    useEffect(() => {
        // change active tab when signaled by redux store (basically when duplicating or removing models)
        if (modelCustomizationPageFocusId !== undefined && modelCustomizationPageFocusId !== currentTreeViewFocus) {

            // see if we can change the actual selection yet, otherwise keep waiting for next updates
            const item = findItemFromHierarchy(treeViewData, modelCustomizationPageFocusId, true);
            if (item) {
                dispatch(modelCustomizationPageFocusCleared());
                setCurrentTreeViewFocus(modelCustomizationPageFocusId);
                handleTreeViewItemSelect(item);
            }
        }
    }, [dispatch, currentTreeViewFocus, modelCustomizationPageFocusId, treeViewData, handleTreeViewItemSelect, setCurrentTreeViewFocus]);

    // Get search text from the appropriate slice based on model type
    const currentSearchText = useSelector((state: StoreState) => 
        getActiveFreeTextFilter(state, modelType)
    );

    const handleSearchTextChange = useCallback((value: string) => {
        dispatch(getFreeTextFilterAction(modelType)(value));
    }, [dispatch, modelType]);

    const filteredCustomizationHierarchy = useMemo(() => {
        return filterCustomizationTreeByFreeText(customizationHierarchy, currentSearchText);
    }, [customizationHierarchy, currentSearchText]);
    // patternfly's TreeView will crash if we try to render a 0-sized data array, so don't render it
    // if we're still initializing data
    const isTreeViewDisplayed = treeViewData.length > 0;

    useEffect(() => {
        const headerItems: TreeViewDataItem[] = BODY_PART_DISPLAY_ORDER.map(bp => ({
            name: t(bp.translationKey),
            id: bp.id,
            children: undefined,
            icon: getBodyPartIcon(bp.id),
        }));

        const data = convertTreeViewDataIntoItemHierarchy(
            filteredCustomizationHierarchy,
            (item, noLicenseWarning, tags, nameOverride) =>
                getTreeViewItemName(
                    item,
                    noLicenseWarning,
                    tags,
                    nameOverride,
                    item.isLatest,
                    item.version || '',
                    includeNonLicensedModelsState,
                    includeDeprecatedModelsState,
                    includeUserHiddenModelsState
                ),
            noLicenseWarning,
            headerItems,
            includeNonLicensedModelsState,
            includeDeprecatedModelsState,
            includeUserHiddenModelsState
        );

        handleTreeViewDataUpdate(data);
    }, [filteredCustomizationHierarchy, noLicenseWarning, handleTreeViewDataUpdate, t, includeNonLicensedModelsState, includeDeprecatedModelsState, includeUserHiddenModelsState]);

    return (
        <DrawerPanelContent hasNoBorder defaultSize="300px">
            <DrawerPanelBody hasNoPadding>
                <div className="mv-compact-tabs">
                    <CustomizationSearchBar value={currentSearchText} onChange={handleSearchTextChange} />
                    <CustomizationFilters modelType={modelType} />
                    {isTreeViewDisplayed && (
                        <TreeView
                            data={treeViewData}
                            onSelect={handleSelect}
                            activeItems={activeTreeViewItems}
                            hasGuides
                            className="customization-tree-view"
                            data-testid="customization-tree-view"
                            useMemo
                        />
                    )}
                </div>
            </DrawerPanelBody>
        </DrawerPanelContent>
    );
}

export default CustomizationSideBar;
